/* eslint-disable max-lines */
import React, {
    useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Play, Loader2, RotateCcw, AlertCircle, CheckCircle2,
    Paperclip, X as XIcon, Plus, Brain, ChevronRight
} from 'lucide-react';
import {
    Button
} from 'shared/components/ui/button';
import {
    cn
} from 'shared/lib/utils';
import {
    unifiedModelWSService
} from 'shared/api/services/PROJECT-O/WEBSOCKETS/UnifiedModel.websocket.service';
import KbFilePicker from './KbFilePicker';

/**
 * InstructionTestPanel — predict an Instruction (LLM) live.
 *
 * Same elegant executor pattern as OpcodeTestPanel + AssistantTestPanel.
 * The stored `prompt` is static text sent to the model exactly as written.
 * Inputs here: a free-text **Query** (the runtime input, joined to the
 * static prompt) plus an optional **Attached file** picked from a
 * Knowledge Base collection.
 *
 * Wire shape (legacy `unifiedModelWSService.predict`):
 *   POST /projecto/config/api/v1/instructions/predict?model_type=<provider>
 *   body { org_id, sub_org_id, unique_name, query, image_urls }
 *   response { result: { prediction: string | object } }
 *
 * Image attachments → `image_urls[]` (vision models). Non-image attachments
 * → an "Attached file: <name> <url>" line is appended to the query text.
 *
 * Layout:
 *   - Default (narrow): inputs on top, result below.
 *   - Maximized: inputs left (440px), result right (flex-1). 2-pane.
 */
export default function InstructionTestPanel({
    instruction, // { unique_name, model_type, prompt, modelId, parameters? }
    maximized = false,
    isDirty = false,
    onSaveBeforeRun = null, // async fn — called before run when dirty
}) {
    const {
        oragID, subOragID
    } = useParams();
    const [query, setQuery] = useState('');
    const [attached, setAttached] = useState(null); // KbFilePicker pick
    const [pickerOpen, setPickerOpen] = useState(false);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null); // { ok, text, raw, error, ts }
    const [error, setError] = useState(null);

    function clearAll() {
        setQuery('');
        setAttached(null);
        setResult(null);
        setError(null);
    }

    function attachedIsImage() {
        return !!attached && isImageFile(attached.file_name, attached.content_type);
    }

    function buildImageUrls() {
        if (!attached || !attached.url) return [];
        return attachedIsImage() ? [attached.url] : [];
    }

    // Non-image attachments have no dedicated wire channel: the file's name
    // and signed URL are appended to the query text so the model can see it.
    function buildQuery() {
        const q = query.trim();
        if (!attached || attachedIsImage()) return q;
        const line = `Attached file: ${attached.file_name || ''} ${attached.url || ''}`.trim();
        return q ? `${q}\n${line}` : line;
    }

    async function run() {
        if (running) return;
        if (!instruction?.unique_name || !instruction?.model_type) {
            setError('Save the instruction first (unique name + model type are required to run predict).');
            return;
        }
        if (!query.trim()) {
            setError('Type a query — that\'s the actual user message the model receives.');
            return;
        }
        setError(null);
        setRunning(true);
        setResult(null);
        const body = {
            org_id: oragID || '',
            sub_org_id: subOragID || '',
            unique_name: instruction.unique_name,
            query: buildQuery(),
            image_urls: buildImageUrls()
        };
        try {
            const res = await unifiedModelWSService.predict(body, instruction.model_type);
            const prediction = res?.result?.prediction;
            setResult({
                text: normalizePrediction(prediction),
                raw: res,
                ts: Date.now()
            });
        } catch (err) {
            setResult({
                error: err?.response?.data?.message
                    || err?.response?.data?.error
                    || err?.message
                    || (typeof err === 'string' ? err : safeStringify(err)),
                ts: Date.now()
            });
        } finally {
            setRunning(false);
        }
    }

    return (
        <div className={cn(
            'flex h-full overflow-hidden bg-card/30',
            maximized ? 'flex-col lg:flex-row' : 'flex-col'
        )}>
            {/* ── INPUTS PANE ───────────────────────────────────────── */}
            <div className={cn(
                'flex flex-col min-h-0',
                maximized ? 'lg:w-[440px] lg:flex-shrink-0 lg:border-r' : 'border-b max-h-[60%]'
            )}>
                {/* Instruction summary */}
                <div className="flex-shrink-0 border-b bg-card px-3 py-2.5">
                    <div className="rounded-md border bg-muted/30 px-2.5 py-1.5">
                        <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-[12.5px] font-semibold text-foreground inline-flex items-center gap-1.5">
                                    <Brain className="h-3.5 w-3.5 text-violet-500" />
                                    {instruction?.unique_name || '(unsaved)'}
                                </div>
                                <div className="truncate text-[11px] text-muted-foreground">
                                    {instruction?.model_type || '—'}
                                    {instruction?.modelId && (
                                        <span className="ml-1 font-mono">· {instruction.modelId}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Query + attachment */}
                <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-4">
                    {error && (
                        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-[12px] text-destructive">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <Section title="Query" hint="the runtime input">
                        <textarea
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="What should the model respond to? e.g. Summarize the attached document in 5 bullets."
                            rows={3}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                    </Section>

                    <Section title="Attachment" hint="optional · file from KB">
                        {attached ? (
                            <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 px-2 py-1.5">
                                <Paperclip className="mt-0.5 h-3 w-3 flex-shrink-0 text-primary" />
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-[12px] font-medium">{attached.file_name}</div>
                                    <div className="truncate font-mono text-[10.5px] text-muted-foreground">
                                        {attached.collection_id}
                                    </div>
                                    {attached.url ? (
                                        <a
                                            href={attached.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-0.5 inline-flex items-center text-[10.5px] text-primary hover:underline"
                                        >
                                            View signed URL ↗
                                        </a>
                                    ) : (
                                        <div className="mt-0.5 text-[10.5px] text-amber-700">
                                            ⚠ no signed URL — the model will not be able to reach this file.
                                        </div>
                                    )}
                                    {isImageFile(attached.file_name, attached.content_type) && (
                                        <span className="mt-0.5 inline-block rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-700">
                                            routed via image_urls[] (vision)
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setAttached(null)}
                                    className="text-muted-foreground hover:text-destructive"
                                    title="Remove"
                                >
                                    <XIcon className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ) : (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPickerOpen(true)}
                                className="w-full justify-start"
                            >
                                <Plus className="mr-1.5 h-3 w-3" /> Pick a file from Knowledge…
                            </Button>
                        )}
                        {attached && (
                            <p className="text-[10.5px] text-muted-foreground">
                                {attachedIsImage()
                                    ? <>Images are sent via <code className="rounded bg-muted px-1">image_urls[]</code> (vision models).</>
                                    : <>Other files: an <code className="rounded bg-muted px-1">Attached file: name url</code> line is appended to the query text.</>}
                            </p>
                        )}
                    </Section>
                </div>

                <div className="flex-shrink-0 border-t bg-background px-3 py-2 flex items-center justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAll}
                        disabled={running || (!query && !attached && !result)}
                    >
                        <RotateCcw className="mr-1 h-3 w-3" /> Clear
                    </Button>
                    <Button
                        size="sm"
                        onClick={async () => {
                            if (isDirty && onSaveBeforeRun) {
                                const ok = await onSaveBeforeRun();
                                if (!ok) return;
                            }
                            run();
                        }}
                        disabled={running || !query.trim() || !instruction?.unique_name}
                    >
                        {running ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Play className="mr-1 h-3 w-3" />}
                        {running ? 'Running' : isDirty && onSaveBeforeRun ? 'Save & Run' : 'Run'}
                    </Button>
                </div>
            </div>

            {/* ── RESULT PANE ───────────────────────────────────────── */}
            <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-muted/10">
                <ResultBlock running={running} result={result} />
            </div>

            <KbFilePicker
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                onPick={setAttached}
            />
        </div>
    );
}

// ─── Sub-components ───────────────────────────────────────────────────

function Section({
    title, hint, children
}) {
    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between border-b border-dashed pb-0.5">
                <h4 className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{title}</h4>
                {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
            </div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function ResultBlock({
    running, result
}) {
    if (!running && !result) {
        return (
            <div className="text-sm text-muted-foreground">
                Type a query, optionally attach a KB file, and click <b>Run</b>. The
                model's prediction will appear here, along with the raw envelope.
            </div>
        );
    }
    if (running) {
        return (
            <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2.5 text-sm italic text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> running predict…
            </div>
        );
    }
    if (result.error) {
        return (
            <div className="space-y-3">
                <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
                    <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] font-semibold text-destructive">Predict failed</div>
                        <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[11.5px] text-destructive">
                            {result.error}
                        </pre>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="space-y-3">
            <div className="rounded-md border border-emerald-300/50 bg-emerald-50/40 px-3 py-2 flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700" />
                <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold text-emerald-800">Completed</div>
                    <div className="text-[10.5px] text-muted-foreground">prediction returned</div>
                </div>
            </div>

            <div className="rounded-md border bg-card px-3 py-2.5">
                <div className="mb-1 flex items-center gap-1.5">
                    <Brain className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">prediction</span>
                </div>
                <pre className="whitespace-pre-wrap break-words text-[12.5px] leading-snug text-foreground">
                    {result.text}
                </pre>
            </div>

            <details className="rounded-md border bg-card px-3 py-2 text-[11.5px]">
                <summary className="cursor-pointer flex items-center gap-1 text-muted-foreground hover:text-foreground">
                    <ChevronRight className="h-3 w-3" />
                    Raw response
                </summary>
                <pre className="mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap break-words rounded border bg-muted/20 px-2 py-1.5 font-mono text-[10.5px]">
                    {safeStringify(result.raw)}
                </pre>
            </details>
        </div>
    );
}

// ─── Helpers ───────────────────────────────────────────────────────

function normalizePrediction(p) {
    if (p == null) return '(no output)';
    if (typeof p === 'string') return p;
    if (typeof p === 'object') {
        // Walk a few common shapes used by IBM/Groq/etc.
        const candidates = [p.generated_text, p.text, p.output, p.result, p.completion];
        for (const c of candidates) {
            if (typeof c === 'string' && c.trim()) return c;
        }
        return safeStringify(p);
    }
    return String(p);
}

function isImageFile(name, contentType) {
    const lower = (name || '').toLowerCase();
    if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower)) return true;
    if (typeof contentType === 'string' && contentType.startsWith('image/')) return true;
    return false;
}

function safeStringify(v) {
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}
